/**
 * The external viewer's one API call (f15.5) -- deliberately NOT in `client.ts`.
 *
 * `client.ts` is the place a bearer token is attached; this module is the one
 * place none ever is. It imports nothing from `@/auth`, sends no Authorization
 * header, and omits credentials, so the password gate can never become a
 * sign-in path. The code travels in the request body and nowhere else.
 */

import { appConfig } from '@/config';

import type { components } from './generated/schema';

export type ExternalAccessGranted = components['schemas']['ExternalAccessGranted'];

/** The one message every refusal carries, whatever the cause (BR-F15.5-03). */
export const UNIFORM_REFUSAL = 'This link or access code is not valid.';

export class ExternalAccessRefused extends Error {
  constructor() {
    super(UNIFORM_REFUSAL);
    this.name = 'ExternalAccessRefused';
  }
}

/** Absolute URL of an API path, e.g. the grant's `content_url`. */
export function externalApiUrl(path: string): string {
  return `${appConfig.apiBaseUrl.replace(/\/+$/, '')}${path}`;
}

export async function enterAccessCode(ref: string, code: string): Promise<ExternalAccessGranted> {
  let response: Response;
  try {
    response = await fetch(externalApiUrl(`/external/artifacts/${encodeURIComponent(ref)}/access`), {
      method: 'POST',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code }),
    });
  } catch {
    throw new ExternalAccessRefused();
  }
  if (!response.ok) throw new ExternalAccessRefused();
  return (await response.json()) as ExternalAccessGranted;
}
